import { useEffect, useState } from "react";
import { Download, Check, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, formatDateTime, telHref } from "@/lib/api";
import { STRUCTURE_LABEL } from "@/lib/content";
import { AdminTitle, StatusSelect } from "./ui";

const TYPES = [["", "Toutes"], ["partenaire", "Partenaires"], ["benevole", "Bénévoles"], ["formation", "Formations"], ["don", "Dons"]];

export function Applications() {
  const [type, setType] = useState("");
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get(`/admin/applications${type ? `?type=${type}` : ""}`).then((r) => setRows(r.data)); }, [type]);
  const setStatus = async (id, status) => {
    await api.patch(`/admin/applications/${id}`, { status });
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
    toast.success("Statut mis à jour");
  };
  return (
    <div data-testid="admin-applications">
      <AdminTitle title="Candidatures">
        <button className="btn-u !py-2" onClick={() => downloadFile(`/admin/applications-export${type ? `?type=${type}` : ""}`, `candidatures-${type || "toutes"}.csv`)} data-testid="applications-export-csv">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </AdminTitle>
      <div className="mb-5 flex flex-wrap gap-2">
        {TYPES.map(([v, l]) => (
          <button key={v} onClick={() => setType(v)} data-testid={`applications-tab-${v || "all"}`}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold ${type === v ? "bg-ink text-white" : "bg-white text-neutral-700 hover:bg-neutral-200"}`}>{l}</button>
        ))}
      </div>
      <div className="space-y-3">
        {rows && !rows.length && <p className="card p-6 text-neutral-500">Aucune candidature.</p>}
        {(rows || []).map((r) => (
          <details key={r.id} className="card group p-4" data-testid={`application-row-${r.id}`}>
            <summary className="flex cursor-pointer list-none flex-wrap items-center gap-3">
              <span className="rounded bg-u-soft px-2 py-0.5 text-xs font-semibold text-u">{r.type_label}</span>
              <span className="font-semibold">{r.name}</span>
              <span className="text-sm text-neutral-500">{STRUCTURE_LABEL[r.structure]} · {formatDateTime(r.created_at)}</span>
              <span className="ml-auto" onClick={(e) => e.preventDefault()}><StatusSelect value={r.status} onChange={(v) => setStatus(r.id, v)} testid={`application-status-${r.id}`} /></span>
            </summary>
            <dl className="mt-4 grid gap-x-6 gap-y-2 border-t pt-4 text-sm sm:grid-cols-2">
              {r.email && <div><dt className="text-neutral-500">E-mail</dt><dd><a href={`mailto:${r.email}`} className="text-u">{r.email}</a></dd></div>}
              {r.phone && <div><dt className="text-neutral-500">Téléphone</dt><dd><a href={telHref(r.phone)} className="text-u">{r.phone}</a></dd></div>}
              {r.country && <div><dt className="text-neutral-500">Pays</dt><dd>{r.country}</dd></div>}
              {Object.entries(r.details || {}).map(([k, v]) => (
                <div key={k}><dt className="capitalize text-neutral-500">{k.replace("_", " ")}</dt><dd>{Array.isArray(v) ? v.join(", ") : v}</dd></div>
              ))}
              {r.message && <div className="sm:col-span-2"><dt className="text-neutral-500">Message</dt><dd className="whitespace-pre-line">{r.message}</dd></div>}
            </dl>
          </details>
        ))}
      </div>
    </div>
  );
}

export function GuestbookAdmin() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/guestbook").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const act = async (id, approved) => { await api.patch(`/admin/guestbook/${id}`, { approved }); toast.success(approved ? "Publié" : "Dépublié"); load(); };
  const del = async (id) => { if (window.confirm("Supprimer ce message ?")) { await api.delete(`/admin/guestbook/${id}`); load(); } };
  return (
    <div data-testid="admin-guestbook">
      <AdminTitle title="Livre d'or — modération" />
      <div className="space-y-3">
        {rows && !rows.length && <p className="card p-6 text-neutral-500">Aucun message.</p>}
        {(rows || []).map((g) => (
          <div key={g.id} className="card flex flex-col gap-3 p-4 sm:flex-row sm:items-start" data-testid={`guestbook-row-${g.id}`}>
            <div className="flex-1">
              <p className="font-semibold">{g.name} {g.city && <span className="font-normal text-neutral-500">— {g.city}</span>}
                <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${g.approved ? "bg-ong text-white" : "bg-gold text-ink"}`}>{g.approved ? "Publié" : "En attente"}</span></p>
              <p className="mt-1 whitespace-pre-line text-neutral-700">{g.message}</p>
            </div>
            <div className="flex gap-2">
              {g.approved ? (
                <button className="btn-outline !px-3 !py-1.5 text-sm" onClick={() => act(g.id, false)} data-testid={`guestbook-unpublish-${g.id}`}><X className="h-4 w-4" /> Dépublier</button>
              ) : (
                <button className="btn-u !px-3 !py-1.5 text-sm" onClick={() => act(g.id, true)} data-testid={`guestbook-approve-${g.id}`}><Check className="h-4 w-4" /> Valider</button>
              )}
              <button className="btn !px-3 !py-1.5 bg-asso text-sm text-white" onClick={() => del(g.id)} aria-label="Supprimer" data-testid={`guestbook-delete-${g.id}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function NewsletterAdmin() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/newsletter").then((r) => setRows(r.data)); }, []);
  return (
    <div data-testid="admin-newsletter">
      <AdminTitle title={`Newsletter (${rows?.length ?? 0})`}>
        <button className="btn-u !py-2" onClick={() => downloadFile("/admin/newsletter-export", "newsletter.csv")} data-testid="newsletter-export-csv"><Download className="h-4 w-4" /> Export CSV</button>
      </AdminTitle>
      <div className="card divide-y">
        {(rows || []).map((n) => (
          <div key={n.id} className="flex justify-between p-3 text-sm"><span>{n.email}</span><span className="text-neutral-500">{STRUCTURE_LABEL[n.preference]} · {formatDateTime(n.created_at)}</span></div>
        ))}
      </div>
    </div>
  );
}
